import React from 'react';

import { useDroppable } from '@dnd-kit/core';

import type { DropZoneInfo } from '../../hooks/useDropZone';
import type { DraggedItem } from '../../hooks/useLineItemDrag';

export interface DroppableSectionProps {
	id: string;
	sectionName?: string;
	children: React.ReactNode;
	isDragging?: boolean;
	isSource?: boolean;
	draggedItemHasChildren?: boolean;
	onItemDrop?: (item: DraggedItem, dropZone: DropZoneInfo) => void;
}

export const DroppableSection: React.FC<DroppableSectionProps> = ({
	id,
	sectionName,
	children,
	isDragging = false,
	isSource = false,
	draggedItemHasChildren = false,
	onItemDrop,
}) => {
	// Always use the id prop directly (which should be category_${sectionName})
	// This ensures compatibility with AnalysisStep's handleDragEnd which expects category_* IDs
	const droppable = useDroppable({
		id, // Use the provided id directly (e.g., category_dues_fees_assessments)
		disabled: isSource,
		data: {
			sectionName: sectionName || id.replace('category_', ''),
			type: 'category',
		},
	});

	const isOver = droppable.isOver;
	const setNodeRef = droppable.setNodeRef;

	const showHighlight = isOver && !isSource;
	const showPotentialZone = isDragging && !isSource;
	const isParentItemDrag = draggedItemHasChildren;
	const isChildItemDrag = !draggedItemHasChildren && isDragging;

	return (
		<div
			ref={setNodeRef}
			className={`transition-all w-full duration-200 ${
				showHighlight
					? isParentItemDrag
						? 'bg-blue-100 ring-4 ring-blue-500 ring-inset shadow-xl'
						: 'bg-blue-50/70 ring-2 ring-blue-400 ring-inset shadow-md'
					: showPotentialZone
						? isParentItemDrag
							? 'bg-blue-50/50 ring-2 ring-blue-300 ring-inset'
							: 'ring-1 ring-gray-200 ring-inset'
						: ''
			}`}
			style={{
				position: 'relative',
			}}
		>
			{/* Drop indicator for sections when dragging parent items with children */}
			{showHighlight && isParentItemDrag && (
				<>
					<div className="absolute top-0 left-4 right-4 h-1 bg-blue-500 rounded-full shadow-sm animate-pulse" />
					<div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full mb-2 flex items-center justify-center pointer-events-none z-10">
						<div className="bg-blue-600 text-white px-6 py-3 rounded-lg shadow-2xl text-base font-bold animate-pulse flex items-center gap-2">
							<span>↓</span>
							<span>Drop to move parent here</span>
							<span>↓</span>
						</div>
					</div>
				</>
			)}
			{/* Drop indicator for child items - show "drop as parent here" message above */}
			{showHighlight && isChildItemDrag && (
				<>
					<div className="absolute top-0 left-4 right-4 h-1 bg-blue-500 rounded-full shadow-sm animate-pulse" />
					<div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full mb-2 flex items-center justify-center pointer-events-none z-10">
						<div className="bg-blue-600 text-white px-6 py-3 rounded-lg shadow-2xl text-base font-bold animate-pulse flex items-center gap-2">
							<span>↓</span>
							<span>Drop as parent here</span>
							<span>↓</span>
						</div>
					</div>
				</>
			)}
			{/* Potential zone indicator for parent items */}
			{showPotentialZone && isParentItemDrag && (
				<div className="absolute top-0 left-0 right-0 text-center py-1 bg-blue-100/70 text-blue-700 text-xs font-medium pointer-events-none border-b border-blue-200">
					Valid section drop zone
				</div>
			)}
			{/* Potential zone indicator for child items */}
			{showPotentialZone && isChildItemDrag && (
				<div className="absolute top-0 left-0 right-0 text-center py-1 bg-blue-100/70 text-blue-700 text-xs font-medium pointer-events-none border-b border-blue-200">
					Drop as parent (root item)
				</div>
			)}
			{children}
		</div>
	);
};
